import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { StoreLayout } from "../../components/StoreLayout";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  ShieldCheck, Plus, Minus, ArrowCounterClockwise, Receipt, CheckCircle, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { openRazorpayCheckout } from "../../lib/razorpay";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function EstimateCalculator() {
  const [params] = useSearchParams();
  const orderId = params.get("id") || "";
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [catalog, setCatalog] = useState(null); // {config, sections}
  const [qty, setQty] = useState({}); // itemId -> number
  const [order, setOrder] = useState(null); // looked-up booking
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null); // {amount}
  const [gstAdd, setGstAdd] = useState(true); // include GST on additional charges (B2B can opt out)

  useEffect(() => {
    api.get("/public/estimate/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Look up the order by ?id= and auto-select its category
  useEffect(() => {
    if (!orderId) return;
    api.get(`/public/estimate/order/${orderId}`).then((r) => {
      if (r.data.found) {
        setOrder(r.data);
        if (r.data.product_category) setCategory(r.data.product_category);
      }
    }).catch(() => {});
  }, [orderId]);

  // Load catalog when category changes
  useEffect(() => {
    if (!category) { setCatalog(null); return; }
    setQty({});
    api.get(`/public/estimate/catalog/${encodeURIComponent(category)}`).then((r) => setCatalog(r.data)).catch(() => {});
  }, [category]);

  const cfg = catalog?.config || {};
  const step = (id, d) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + d) }));
  const setQ = (id, v) => setQty((q) => ({ ...q, [id]: Math.max(0, Number(v) || 0) }));

  const calc = useMemo(() => {
    const pct = Number(cfg.gst_percent || 0);
    let subtotal = 0, gstItemsRaw = 0;
    (catalog?.sections || []).forEach((s) => s.items.forEach((it) => {
      const amt = (qty[it.id] || 0) * it.rate;
      subtotal += amt;
      const ig = (it.gst_percent === null || it.gst_percent === undefined) ? pct : Number(it.gst_percent || 0);
      const apply = it.gst_required || gstAdd;   // mandatory always; optional follows the tick
      if (apply) gstItemsRaw += amt * ig / 100;
    }));
    const fixed = Number(cfg.fixed_charge || 0);
    const fixedApply = cfg.fixed_gst_required !== false || gstAdd;
    const gstFixed = fixedApply ? Math.round(fixed * pct / 100 * 100) / 100 : 0;
    const gstItems = Math.round(gstItemsRaw * 100) / 100;
    const gst = Math.round((gstFixed + gstItems) * 100) / 100;
    const total = Math.round((subtotal + fixed + gst) * 100) / 100;
    return { subtotal: Math.round(subtotal * 100) / 100, fixed, gstFixed, gstItems, gst, total };
  }, [qty, catalog, cfg, gstAdd]);

  // Show the opt-in tick only when there is at least one OPTIONAL GST element.
  const hasOptionalGst = useMemo(() => {
    const pct = Number(cfg.gst_percent || 0);
    const fixedOptional = cfg.fixed_charge > 0 && pct > 0 && cfg.fixed_gst_required === false;
    const itemOptional = (catalog?.sections || []).some((s) => s.items.some((it) => {
      const ig = (it.gst_percent === null || it.gst_percent === undefined) ? pct : Number(it.gst_percent || 0);
      return ig > 0 && !it.gst_required;
    }));
    return fixedOptional || itemOptional;
  }, [catalog, cfg]);

  const anyGst = useMemo(() =>
    Number(cfg.gst_percent || 0) > 0 ||
    (catalog?.sections || []).some((s) => s.items.some((it) => Number(it.gst_percent || 0) > 0)),
  [catalog, cfg]);

  const lines = () => Object.entries(qty).filter(([, q]) => q > 0).map(([item_id, q]) => ({ item_id, qty: q }));

  const payNow = async () => {
    const ln = lines();
    if (calc.total <= 0) { toast.error("Nothing to pay for this estimate."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/estimate/create-order", {
        job_id: order?.job_id || orderId || "", product_category: category, lines: ln,
        name: order?.customer_name || "", email: "", phone: "", apply_gst_additional: gstAdd,
      });
      const res = await openRazorpayCheckout(
        { razorpay_order_id: data.razorpay_order_id, amount: data.amount, key_id: data.key_id, prefill: data.prefill },
        { description: `${category} — additional charges` });
      await api.post("/public/estimate/verify", {
        order_ref: data.order_ref, razorpay_order_id: data.razorpay_order_id,
        razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
      setPaid({ amount: data.amount });
    } catch (e) {
      toast.error(e?.message === "Payment cancelled" ? "Payment cancelled" : apiError(e.response?.data?.detail) || "Payment failed");
    } finally { setBusy(false); }
  };

  if (paid) {
    return (
      <StoreLayout>
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="estimate-paid">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle size={44} weight="fill" className="text-green-600" />
          </div>
          <h1 className="font-[Outfit] text-3xl font-extrabold tracking-tight mt-6">Payment Successful</h1>
          <p className="text-[#475569] mt-3">You paid <strong>{inr(paid.amount)}</strong>. A receipt has been recorded against your booking. Thank you!</p>
          <Button className="rounded-xl mt-8 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={() => (window.location.href = "/")}>Back to Home</Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="bg-[#1E293B] text-white">
        <div className="max-w-4xl mx-auto px-5 py-12">
          <div className="flex items-center gap-2 text-[#C7D2FE]"><ShieldCheck size={22} weight="fill" /><span className="uppercase text-xs tracking-[0.2em] font-semibold">Fixigo Fair-Price</span></div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Know the real price before you pay</h1>
          <p className="text-white/70 mt-3 max-w-2xl">Enter the items your engineer quoted. We'll calculate the official Fixigo rate so you're never overcharged.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        {order && (
          <div className="bg-[#eef2ff] border border-[#c7d2fe] rounded-2xl p-4" data-testid="estimate-order-info">
            <p className="text-xs uppercase tracking-wide text-[#4F46E5] font-bold">Your booking</p>
            <p className="font-[Outfit] font-bold mt-1">{order.service_type} · {order.product_category}</p>
            <p className="text-sm text-[#475569]">{order.customer_name}{order.amount ? ` · Base amount ${inr(order.amount)}` : ""}</p>
            {order.b2b_partner_order_id && (
              <p className="text-sm text-[#475569] mt-0.5" data-testid="estimate-b2b-order-no">
                Order No: <strong className="text-[#1E293B]">{order.b2b_partner_order_id}</strong>
                {order.b2b_partner_name ? ` · ${order.b2b_partner_name}` : ""}
              </p>
            )}
          </div>
        )}

        {order?.additional_charges_payment_to === "partner" && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800" data-testid="estimate-partner-collect-note">
            <strong>Note:</strong> Additional spare/part charges for this booking are collected directly by the service technician. Only the standard one-time charge is payable online here.
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wide text-[#475569] font-bold">Select the service</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-xl mt-1.5 h-11" data-testid="estimate-category-select"><SelectValue placeholder="Choose a service category" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.product_category} value={c.product_category}>{c.product_category} ({c.item_count} items)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {catalog && (
            <Button variant="outline" className="rounded-xl gap-2 h-11" onClick={() => setQty({})} data-testid="estimate-reset">
              <ArrowCounterClockwise size={16} weight="bold" /> Reset
            </Button>
          )}
        </div>

        {cfg.fixed_charge > 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800" data-testid="estimate-fixed-note">
            <Info size={18} weight="bold" className="shrink-0 mt-0.5" />
            <span>A {cfg.fixed_charge_label || "one-time charge"} of <strong>{inr(cfg.fixed_charge)}</strong> applies to every job (GST included) and is part of the total below.</span>
          </div>
        )}

        {hasOptionalGst && (
          <label className="flex items-start gap-3 bg-white border border-[#E2E8F0] rounded-xl p-4 cursor-pointer select-none" data-testid="estimate-gst-toggle-label">
            <input type="checkbox" checked={gstAdd} onChange={(e) => setGstAdd(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded accent-[#4F46E5]" data-testid="estimate-gst-toggle" />
            <span className="text-sm">
              <span className="font-semibold text-[#1E293B]">Include GST on the optional spares/parts</span>
              <span className="block text-xs text-[#64748b] mt-0.5">Untick to pay without GST on the optional items. Items marked GST-mandatory always include GST.</span>
            </span>
          </label>
        )}

        {catalog && catalog.sections.map((sec) => (
          <div key={sec.section} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid={`estimate-section-${sec.section.replace(/\s+/g, "-")}`}>
            <div className="bg-[#F8FAFC] px-5 py-3 border-b border-[#E2E8F0]"><h3 className="font-[Outfit] font-bold text-[#1E293B]">{sec.section}</h3></div>
            <div className="divide-y divide-[#F1F5F9]">
              {sec.items.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid={`estimate-item-${it.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1E293B] text-sm">{it.name}</p>
                    {it.description && <p className="text-xs text-[#94a3b8]">{it.description}</p>}
                    <p className="text-xs text-[#4F46E5] font-semibold mt-0.5">{inr(it.rate)} <span className="text-[#94a3b8] font-normal">{it.unit}</span>
                      {(it.gst_required || gstAdd) && Number(it.gst_percent ?? cfg.gst_percent ?? 0) > 0 && <span className="text-[#94a3b8] font-normal"> · +{Number(it.gst_percent ?? cfg.gst_percent)}% GST{it.gst_required ? "" : ""}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => step(it.id, -1)} className="w-8 h-8 rounded-lg border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F1F5F9] disabled:opacity-40" disabled={!(qty[it.id] > 0)} data-testid={`estimate-minus-${it.id}`}><Minus size={14} weight="bold" /></button>
                    <input value={qty[it.id] || 0} onChange={(e) => setQ(it.id, e.target.value)} className="w-12 h-8 text-center rounded-lg border border-[#E2E8F0] text-sm font-semibold" data-testid={`estimate-qty-${it.id}`} />
                    <button onClick={() => step(it.id, 1)} className="w-8 h-8 rounded-lg border border-[#4F46E5] text-[#4F46E5] flex items-center justify-center hover:bg-[#eef2ff]" data-testid={`estimate-plus-${it.id}`}><Plus size={14} weight="bold" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {!category && <p className="text-center text-[#94a3b8] py-10">Select a service above to see the official rate list.</p>}
      </div>

      {catalog && (
        <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
            <div className="text-sm">
              <div className="flex items-center gap-3 text-[#475569] flex-wrap">
                <span>Items {inr(calc.subtotal)}</span>
                {calc.fixed > 0 && <span>· {cfg.fixed_charge_label} {inr(calc.fixed)}</span>}
                {anyGst && <span>· GST {inr(calc.gst)}{!gstAdd && calc.gstItems >= 0 ? "" : ""}</span>}
              </div>
              <div className="font-[Outfit] text-2xl font-extrabold text-[#1E293B]" data-testid="estimate-total">{inr(calc.total)}</div>
            </div>
            <Button className="rounded-xl h-12 px-6 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold gap-2 disabled:opacity-60" onClick={payNow} disabled={busy || calc.total <= 0} data-testid="estimate-pay-btn">
              <Receipt size={18} weight="bold" /> {busy ? "Processing…" : "Pay Now"}
            </Button>
          </div>
        </div>
      )}
    </StoreLayout>
  );
}
